import type { ThreadMessage } from '@/lib/types';

const now = Date.now();

// MOCK DATA — keyed by runId. The first pinned run has a richer transcript
// so reviewers can see stage transitions + multiple agents + founder replies.
export const MOCK_THREAD_MESSAGES: Record<string, ThreadMessage[]> = {
  run_mock_pinned_1: [
    {
      id: 'm1',
      runId: 'run_mock_pinned_1',
      sender: 'System',
      text: 'Run created — customer: Acme Coffee Roasters',
      createdAtMs: now - 1000 * 60 * 60 * 3,
      isStageTransition: true,
    },
    {
      id: 'm2',
      runId: 'run_mock_pinned_1',
      sender: 'Discovery',
      text: "Hi — I'm here to understand your business so we can build you an Infinity. What kind of business do you run?",
      createdAtMs: now - 1000 * 60 * 60 * 3 + 1000 * 5,
    },
    {
      id: 'm3',
      runId: 'run_mock_pinned_1',
      sender: 'Discovery',
      text: 'Two coffee-roasting cafés in Toronto, 18 staff across both locations, and you want to tighten the inventory + scheduling flow. Did I get that right?',
      createdAtMs: now - 1000 * 60 * 60 * 2 - 1000 * 120,
    },
    {
      id: 'm4',
      runId: 'run_mock_pinned_1',
      sender: 'System',
      text: 'Discovery stage complete — requirements synthesized.',
      createdAtMs: now - 1000 * 60 * 60 * 2,
      isStageTransition: true,
    },
    {
      id: 'm5',
      runId: 'run_mock_pinned_1',
      sender: 'Oracle',
      text: 'Feasibility assessment: 3 GCP APIs needed (Cloud Run, Firestore, Cloud Scheduler). All whitelisted. Estimated monthly cost: $142. One amber flag: Square POS webhook needs manual setup. Recommend proceeding.',
      createdAtMs: now - 1000 * 60 * 20,
    },
    {
      id: 'm6',
      runId: 'run_mock_pinned_1',
      sender: 'System',
      text: '✓ Oracle stage complete — awaiting founder approval',
      createdAtMs: now - 1000 * 60 * 12,
      isStageTransition: true,
    },
  ],
  run_mock_pinned_2: [
    {
      id: 'm1',
      runId: 'run_mock_pinned_2',
      sender: 'System',
      text: 'Run created — customer: Maple Leaf Plumbing',
      createdAtMs: now - 1000 * 60 * 60 * 8,
      isStageTransition: true,
    },
    {
      id: 'm2',
      runId: 'run_mock_pinned_2',
      sender: 'Discovery',
      text: 'Caught the key flows: dispatch scheduling, invoice auto-follow-up, and recurring-job reminders. Synthesizing requirements now.',
      createdAtMs: now - 1000 * 60 * 60 * 7,
    },
    {
      id: 'm3',
      runId: 'run_mock_pinned_2',
      sender: 'Oracle',
      text: 'Feasibility: green across the board. Cost estimate $89/mo.',
      createdAtMs: now - 1000 * 60 * 60 * 5,
    },
    {
      id: 'm4',
      runId: 'run_mock_pinned_2',
      sender: 'Founder',
      text: 'Looks good — approved.',
      createdAtMs: now - 1000 * 60 * 60 * 4 - 1000 * 30,
    },
    {
      id: 'm5',
      runId: 'run_mock_pinned_2',
      sender: 'Logos',
      text: 'Deployment spec: 3 Cloud Run services (dispatch-api, invoice-worker, reminder-cron), 2 Firestore collections, 1 PubSub topic for invoice events. Twilio integration for SMS reminders.',
      createdAtMs: now - 1000 * 60 * 50,
    },
    {
      id: 'm6',
      runId: 'run_mock_pinned_2',
      sender: 'System',
      text: '✓ Logos stage complete — awaiting founder approval',
      createdAtMs: now - 1000 * 60 * 45,
      isStageTransition: true,
    },
  ],
  run_mock_active_1: [
    {
      id: 'm1',
      runId: 'run_mock_active_1',
      sender: 'Discovery',
      text: "Hi — I'm here to understand your business. Tell me a bit about what you do.",
      createdAtMs: now - 1000 * 60 * 50,
    },
    {
      id: 'm2',
      runId: 'run_mock_active_1',
      sender: 'Discovery',
      text: "Got it — we'll focus on shift scheduling and oil-change inventory.",
      createdAtMs: now - 1000 * 60 * 4,
    },
  ],
  run_mock_active_2: [
    {
      id: 'm1',
      runId: 'run_mock_active_2',
      sender: 'System',
      text: 'Run created — customer: Northside Dental',
      createdAtMs: now - 1000 * 60 * 60 * 26,
      isStageTransition: true,
    },
    {
      id: 'm2',
      runId: 'run_mock_active_2',
      sender: 'System',
      text: '✓ Discovery → Oracle → Logos complete',
      createdAtMs: now - 1000 * 60 * 60 * 2,
      isStageTransition: true,
    },
    {
      id: 'm3',
      runId: 'run_mock_active_2',
      sender: 'Genesis',
      text: 'Generating Terraform — 4/11 modules emitted. Next: patient-intake-api module.',
      createdAtMs: now - 1000 * 60 * 20,
    },
  ],
  run_mock_active_3: [
    {
      id: 'm1',
      runId: 'run_mock_active_3',
      sender: 'Oracle',
      text: 'Hold — customer needs to confirm which POS their current flow uses before I can finalize feasibility.',
      createdAtMs: now - 1000 * 60 * 60 * 6,
    },
    {
      id: 'm2',
      runId: 'run_mock_active_3',
      sender: 'System',
      text: 'Run paused by founder',
      createdAtMs: now - 1000 * 60 * 60 * 6 + 1000 * 90,
      isStageTransition: true,
    },
  ],
  run_mock_archived_1: [
    {
      id: 'm1',
      runId: 'run_mock_archived_1',
      sender: 'Discovery',
      text: 'Customer inquiry — 6-chair barbershop looking at booking + loyalty.',
      createdAtMs: now - 1000 * 60 * 60 * 24 * 5,
    },
    {
      id: 'm2',
      runId: 'run_mock_archived_1',
      sender: 'System',
      text: 'Run archived — customer deferred until Q3.',
      createdAtMs: now - 1000 * 60 * 60 * 24 * 3,
      isStageTransition: true,
    },
  ],
};
